import { isTerminalBuildState, BuildStates } from "~/constants/BuildStates";
import Icon from "~/components/shared/Icon";
import CopyButton from "~/components/shared/CopyButton";
import { shortCommit } from "~/lib/commits";
import { twMerge } from "tailwind-merge";

interface Props {
  commitId?: string;
  commitShortLength?: number;
  commitUrl?: string;
  mergeQueueBaseBranch: string | null;
  project: {
    provider: {
      frontendIcon?: string;
      url?: string;
      id?: string;
    };
  };
  state: BuildStates;
  className?: string;
}

export function Commit({
  commitId,
  commitShortLength,
  commitUrl,
  project,
  state,
  mergeQueueBaseBranch,
  className,
}: Props) {
  if (!commitId) {
    return null;
  }

  const shouldLinkToMergeQueue = mergeQueueBaseBranch && !isTerminalBuildState(state);

  let title;
  if (project.provider.id === "github") {
    if (shouldLinkToMergeQueue) {
      title = `View in merge queue for "${mergeQueueBaseBranch}" on GitHub`;
    } else {
      title = `View commit #${commitId} on GitHub`;
    }
  } else if (project.provider.id === "gitlab") {
    title = `View commit #${commitId} on GitLab`;
  } else {
    title = `View commit ${commitId}`;
  }

  const href = shouldLinkToMergeQueue
    ? `${project.provider.url}/queue/${mergeQueueBaseBranch}`
    : commitUrl;

  const commitContent = href ? (
    <a
      title={title}
      href={href}
      className={twMerge("color-inherit hover-color-inherit flex items-center")}
    >
      <Icon
        icon={`custom/solid/provider-${project.provider.frontendIcon}`}
        className="mr-1 size-4 shrink-0"
      />
      <span className="max-w-full truncate font-mono text-[90%]">
        {shortCommit(commitId, commitShortLength)}
      </span>
    </a>
  ) : (
    <span title={commitId} className="font-mono text-[90%]">
      {shortCommit(commitId, commitShortLength)}
    </span>
  );

  if (Features.BuildSidebar) {
    return (
      <CopyButton
        title="Copy commit"
        overlay={true}
        iconOnly={true}
        size="xs"
        contentToCopy={commitId}
        className={className}
      >
        {commitContent}
      </CopyButton>
    );
  }

  return commitContent;
}
